// @ts-nocheck
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import translate from "@insite/client-framework/Translate";
import { DataTableCellProps } from "@insite/mobius/DataTable/DataTableCell";
import { DataTableRowProps } from "@insite/mobius/DataTable/DataTableRow";
import { IconPresentationProps } from "@insite/mobius/Icon";
import Check from "@insite/mobius/Icons/Check";
import { Clickable, DataTableCell, DataTableRow, Icon, TextField, VisuallyHidden } from "@insite/mobius/index";
import getColor from "@insite/mobius/utilities/getColor";
// eslint-disable-next-line spire/fenced-imports
import { ClickableProps } from "@insite/mobius/Clickable";
import RotateCcw from "@insite/mobius/Icons/RotateCcw";
import Close from "@store/Library/Components/Toolkit/Icons/Close";
import { CustomerPoNumber } from "@store/Framework/Services/CustomerPoNumberService";
import useEditManageDefaultPO from "@store/Library/Widgets/ManageDefaultPO/useEditManageDefaultPO";
import DatePicker, { DatePickerPresentationProps } from "@store/MobiusOverrides/DatePicker/DatePicker";
import useExpandProps from "@toolkit/Framework/Common/Hooks/useExpandProps";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";
import React from "react";
import { css } from "styled-components";

interface ManageDefaultPOEditingRowStyles {
    rowStyles?: DataTableRowProps;
    datePicker?: DatePickerPresentationProps;
    buttonCell?: DataTableCellProps;
    savePoButton?: IconPresentationProps;
    cancelChangeButton?: IconPresentationProps;
    textFieldTableCell?: DataTableCellProps;
    confirmClickable?: ClickableProps;
}

const manageDefaultPOEditingRowStyles: ManageDefaultPOEditingRowStyles = {
    confirmClickable: {
        css: css`
            :disabled {
                cursor: not-allowed;
                opacity: 0.4;
            }
        `,
    },
    textFieldTableCell: {
        css: css`
            padding-left: 0;
        `,
    },
    buttonCell: {
        css: css`
            padding-inline: 0;
            display: flex;
            align-items: center;
            justify-content: space-evenly;
        `,
    },
    datePicker: {
        cssOverrides: {
            inputSelect: css`
                padding: ${pxToRem(2)} ${pxToRem(10)};
            `,
        },
    },
    savePoButton: {
        src: Check,
        size: 25,
        css: css`
            > svg {
                stroke-width: 2;
            }
            color: ${getColor("primary")};
            :hover {
                color: ${getColor("text.linkHover")};
            }
        `,
    },
    cancelChangeButton: {
        src: RotateCcw,
        size: 25,
        css: css`
            > svg {
                stroke-width: 2;
            }
            color: ${getColor("primary")};
            :hover {
                color: ${getColor("text.linkHover")};
            }
        `,
    },
};
export interface ManageDefaultPOEditingRowProps {
    customerPoNumber: CustomerPoNumber;
    extendedStyles?: ManageDefaultPOEditingRowStyles;
    overrideStyles?: ManageDefaultPOEditingRowStyles;
    isNew?: boolean;
    onSave: (customerPoNumber: CustomerPoNumber) => void;
    onCancel: () => void;
    validateEndDate: (startDate: Date, endDate: Date | null) => boolean;
    validateStartDate: (startDate: Date, endDate: Date | null) => boolean;
}

const ManageDefaultPOEditingRow: React.FC<ManageDefaultPOEditingRowProps> = ({
    customerPoNumber,
    extendedStyles,
    overrideStyles,
    onSave,
    onCancel,
    validateStartDate,
    validateEndDate,
    isNew = false,
}) => {
    const styles = useExpandProps(manageDefaultPOEditingRowStyles, extendedStyles, overrideStyles);
    const {
        poNumber,
        setPoNumber,
        startDate,
        endDate,
        handleStartDateChange,
        handleEndDateChange,
        handleSubmit,
        isValidSubmit,
        hasSubmitted,
    } = useEditManageDefaultPO(customerPoNumber, onSave, validateEndDate, validateStartDate);

    return (
        <>
            <DataTableRow data-test-selector="Po_editing_row" {...styles.rowStyles}>
                <DataTableCell {...styles.textFieldTableCell}>
                    <TextField
                        id="poNumber_input"
                        error={hasSubmitted ? !poNumber : false}
                        value={poNumber}
                        onChange={event => setPoNumber(event.currentTarget.value)}
                    />
                </DataTableCell>
                <DataTableCell>
                    <DatePicker
                        error={hasSubmitted ? !validateStartDate(startDate, endDate || null) : false}
                        onDayChange={handleStartDateChange}
                        selectedDay={startDate}
                        dateTimePickerProps={{
                            clearIcon: null,
                            maxDate: endDate,
                        }}
                        {...styles.datePicker}
                    />
                </DataTableCell>
                <DataTableCell>
                    <DatePicker
                        error={hasSubmitted ? !validateEndDate(startDate, endDate || null) : false}
                        onDayChange={handleEndDateChange}
                        selectedDay={endDate || undefined}
                        dateTimePickerProps={{
                            clearIcon: null,
                            minDate: startDate,
                        }}
                        {...styles.datePicker}
                    />
                </DataTableCell>
                <DataTableCell>
                    <StyledWrapper {...styles.buttonCell}>
                        <Clickable
                            disabled={hasSubmitted ? !isValidSubmit : false}
                            onClick={handleSubmit}
                            data-test-selector="PoOverride_savePoButton"
                            {...styles.confirmClickable}
                        >
                            <Icon {...styles.savePoButton} />
                            <VisuallyHidden>{translate("save changes")}</VisuallyHidden>
                        </Clickable>
                        <Clickable onClick={onCancel} data-test-selector="PoOverride_cancelPoButton">
                            <Icon {...styles.cancelChangeButton} src={isNew ? Close : RotateCcw} />
                            <VisuallyHidden>{translate("Cancel this Edit")}</VisuallyHidden>
                        </Clickable>
                    </StyledWrapper>
                </DataTableCell>
            </DataTableRow>
        </>
    );
};

export default ManageDefaultPOEditingRow;
